import React from "react";
import { motion } from "framer-motion";
import {
  Building2,
  FileText,
  Scale,
  Calculator,
  Shield,
  Users,
  Globe,
  Handshake,
} from "lucide-react";

const SERVICES = [
  {
    icon: Building2,
    title: "Company Formation",
    copy: "Mainland LLCs, Freezone entities, offshore, branch offices and holding structures.",
  },
  {
    icon: FileText,
    title: "License Renewal & Amendment",
    copy: "Annual renewals, activity additions, share capital changes, shareholder updates.",
  },
  {
    icon: Scale,
    title: "Legal & Compliance",
    copy: "MoA/AoA drafting, UBO, ESR, AML, data-protection and ongoing regulatory filings.",
  },
  {
    icon: Calculator,
    title: "Accounting & VAT",
    copy: "Bookkeeping, VAT registration & returns, corporate tax filings, audit support.",
  },
  {
    icon: Shield,
    title: "Compliance & ESR",
    copy: "Economic Substance, anti-money laundering (AML), and governance policies.",
  },
  {
    icon: Users,
    title: "PRO & Government Liaison",
    copy: "Ministry of Labour, immigration, Tasheel and chamber of commerce services.",
  },
  {
    icon: Globe,
    title: "Trademark & IP",
    copy: "Trademark registration in UAE & GCC, patent filings, IP licensing agreements.",
  },
  {
    icon: Handshake,
    title: "Nominee & Corporate Services",
    copy: "Nominee directors, registered agents, virtual offices and mail handling.",
  },
];

export default function ServicesGrid() {
  return (
    <section
      data-testid="services-grid"
      className="relative bg-[#0A1128] text-white py-20 md:py-32 overflow-hidden"
    >
      <div className="absolute inset-0 grain pointer-events-none" />
      <div
        className="absolute -top-24 left-1/2 -translate-x-1/2 w-[800px] h-[800px] rounded-full bg-[#3366FF]/10 blur-3xl pointer-events-none"
        aria-hidden
      />
      <div className="container-x relative">
        <div className="max-w-3xl">
          <p className="overline text-[#6B8AFF]">What we deliver</p>
          <div className="gold-rule mt-5 mb-6" />
          <h2 className="font-heading font-light tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-tight">
            A full corporate services suite,{" "}
            <span className="font-black text-[#6B8AFF]">under one roof</span>.
          </h2>
          <p className="mt-5 text-slate-300/85 text-base md:text-lg leading-relaxed">
            Incorporation is just the beginning. StartINC supports your business for its
            entire lifecycle in the UAE — from first license to exit.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {SERVICES.map(({ icon: Icon, title, copy }, i) => (
            <motion.div
              key={title}
              data-testid={`service-${i}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.45, delay: (i % 4) * 0.06 }}
              className="card-dark rounded-sm p-6 md:p-7 h-full group"
            >
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-sm border border-white/15 bg-white/5 inline-flex items-center justify-center text-[#6B8AFF] group-hover:border-[#3366FF]/70 group-hover:text-white transition-colors">
                  <Icon className="w-5 h-5" strokeWidth={1.5} />
                </div>
                <span className="font-heading font-black text-sm text-white/15 group-hover:text-[#3366FF]/40 transition-colors">
                  {String(i + 1).padStart(2, "0")}
                </span>
              </div>
              <h3 className="mt-5 font-heading font-semibold text-lg tracking-tight">
                {title}
              </h3>
              <p className="mt-2 text-sm text-slate-300/80 leading-relaxed">{copy}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
